/**
 * Length Role - Convert Pillar
 *
 * Conversão entre unidades de comprimento usando arquitetura hub-and-spoke.
 * Base: meter (m)
 *
 * @example
 * import { convertLength, toBaseLength, fromBaseLength } from '@attrx/role-morphic/length/convert';
 *
 * convertLength('kilometer', 'mile', 1);     // 0.621371...
 * toBaseLength('kilometer', 1);              // 1000
 * fromBaseLength('mile', 1000);              // 0.621371...
 */

import { LENGTH_UNITS, type LengthUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export type Result<T> =
  | { ok: true; value: T }
  | { ok: false; error: string };

// =============================================================================
// CONSTANTS
// =============================================================================

/** Variante base para conversões */
export const LENGTH_BASE: LengthUnit = "meter";

// =============================================================================
// CONVERT
// =============================================================================

/**
 * Converte valor para a unidade base (meter)
 *
 * @param variant - Unidade de origem
 * @param value - Valor a converter
 * @returns Valor em meters
 *
 * @example
 * toBaseLength('kilometer', 1);  // 1000
 * toBaseLength('mile', 1);       // 1609.344
 */
export function toBaseLength(variant: LengthUnit, value: number): number {
  const unit = LENGTH_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown length variant: ${variant}`);
  }

  return value * (unit.factor ?? 1);
}

/**
 * Converte valor da unidade base (meter) para outra unidade
 *
 * @param variant - Unidade de destino
 * @param baseValue - Valor em meters
 * @returns Valor na unidade de destino
 *
 * @example
 * fromBaseLength('kilometer', 1000);  // 1
 * fromBaseLength('mile', 1609.344);   // 1
 */
export function fromBaseLength(variant: LengthUnit, baseValue: number): number {
  const unit = LENGTH_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown length variant: ${variant}`);
  }

  return baseValue / (unit.factor ?? 1);
}

/**
 * Converte entre duas unidades de comprimento
 *
 * @param from - Unidade de origem
 * @param to - Unidade de destino
 * @param value - Valor a converter
 * @returns Valor convertido
 *
 * @example
 * convertLength('kilometer', 'mile', 1);     // 0.621371...
 * convertLength('foot', 'meter', 1);         // 0.3048
 */
export function convertLength(
  from: LengthUnit,
  to: LengthUnit,
  value: number
): number {
  // Otimização: mesma unidade
  if (from === to) {
    return value;
  }

  // Hub-and-spoke: from → base → to
  const baseValue = toBaseLength(from, value);
  return fromBaseLength(to, baseValue);
}

/**
 * Versão safe de convertLength que retorna Result
 *
 * @example
 * tryConvertLength('kilometer', 'mile', 1);
 * // { ok: true, value: 0.621371... }
 *
 * tryConvertLength('invalid', 'mile', 1);
 * // { ok: false, error: 'Unknown length variant: invalid' }
 */
export function tryConvertLength(
  from: LengthUnit,
  to: LengthUnit,
  value: number
): Result<number> {
  try {
    const result = convertLength(from, to, value);
    return { ok: true, value: result };
  } catch (err) {
    return { ok: false, error: String(err) };
  }
}

/**
 * Verifica se uma variante existe
 */
export function hasLengthVariant(variant: string): variant is LengthUnit {
  return variant in LENGTH_UNITS;
}

/**
 * Lista todas as variantes disponíveis
 */
export function getLengthVariants(): LengthUnit[] {
  return Object.keys(LENGTH_UNITS) as LengthUnit[];
}
